import {
    getEnabledSettingStatuses,
    getNormalizedFilter,
    PortalStore,
    portalStoreToken,
} from '@servicetitan/cp-ui';
import { inject, injectable } from '@servicetitan/react-ioc';
import { SettingsApi } from 'api';
import { makeObservable, observable, runInAction } from 'mobx';
import { EstimateStatus } from '../api/portal-estimates.api';
import { EstimatesFilterSettings } from '../utils/estimates-filters';

export interface EstimateSettingsFilters {
    open: boolean;
    sold: boolean;
    dismissed: boolean;
}

export type EstimateSettingsFiltersRecord = Pick<
    EstimateSettingsFilters,
    keyof EstimateSettingsFilters
>;

@injectable()
export class EstimatesFiltersStore {
    @observable settingsFilter?: EstimateSettingsFilters;

    private loading = false;

    constructor(
        @inject(portalStoreToken) private portalStore: PortalStore,
        @inject(SettingsApi) private settingsApi: SettingsApi
    ) {
        makeObservable(this);
    }

    async init() {
        if (this.loading) {
            return;
        }

        this.loading = true;

        const settingsFilter = await this.loadEstimatesSettingsFilters();

        this.loading = false;
        runInAction(() => {
            this.settingsFilter = settingsFilter;
        });
    }

    loadEstimatesSettingsFilters = async (): Promise<EstimateSettingsFilters> => {
        const defaultEstimateFilter: EstimateSettingsFilters = {
            open: false,
            sold: false,
            dismissed: false,
        };
        try {
            const specialSettingsKey = 'estimate-filter';
            const result = await this.settingsApi.client.getSpecialSettings(specialSettingsKey);
            const filter = getNormalizedFilter(result);
            return {
                ...defaultEstimateFilter,
                ...filter,
            };
        } catch (ex) {
            return defaultEstimateFilter;
        }
    };

    getEnabledEstimateStatuses = (): EstimateStatus[] => {
        if (!this.portalStore.user) {
            // Show all statuses for loginless users
            return Object.keys(EstimatesFilterSettings).map(key => Number(key));
        }

        return getEnabledSettingStatuses(
            EstimatesFilterSettings,
            this.settingsFilter as EstimateSettingsFiltersRecord
        );
    };
}
